import React, { useRef, useState } from 'react';
import InsightsFixedTip from './insightsFixedTip';

export interface InsightsTrendHoverPoint {
    matchId: number;
    label: string;
    value: number;
    opponentName?: string;
    when?: string;
    resultLine?: string;
}

interface Props {
    points: InsightsTrendHoverPoint[];
    /** When true, Y is 0–100 (percent metrics). */
    percentScale?: boolean;
    /** Hover label for the plotted value (Goals, Shooting %, …). */
    valueLabel?: string;
}

/**
 * Tiny match-by-match sparkline. No chart library - same spirit as ScoreEvolutionChart.
 */
const InsightsPlayerTrendChart: React.FC<Props> = ({ points, percentScale, valueLabel }) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const chartRef = useRef<SVGSVGElement>(null);

    if (points.length === 0) {
        return <div className="insightsEmpty">No matches in this sample.</div>;
    }

    const WIDTH = 640;
    const HEIGHT = 160;
    const PAD_X = 28;
    const PAD_TOP = 16;
    const PAD_BOTTOM = 28;
    const values = points.map((p) => p.value);
    const minV = percentScale ? 0 : Math.min(0, ...values);
    const maxV = percentScale ? 100 : Math.max(1, ...values);
    const range = Math.max(1, maxV - minV);
    const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM;
    const stepX = points.length === 1 ? 0 : (WIDTH - PAD_X * 2) / (points.length - 1);
    const xAt = (i: number): number => (points.length === 1 ? WIDTH / 2 : PAD_X + i * stepX);
    const yAt = (v: number): number => PAD_TOP + ((maxV - v) / range) * plotH;

    const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.value)}`).join(' ');
    const hover = hoverIndex != null ? points[hoverIndex] : null;
    const valueText =
        hover == null ? '' : percentScale ? `${Math.round(hover.value)}%` : String(hover.value);

    return (
        <div className="insightsTrendChart">
            {hover && hoverIndex != null ? (
                <InsightsFixedTip
                    x={xAt(hoverIndex) / WIDTH}
                    y={yAt(hover.value) / HEIGHT}
                    container={chartRef.current}
                    className="insightsChartTooltip"
                >
                    <b>{hover.opponentName ? `vs ${hover.opponentName}` : hover.label}</b>
                    <i>
                        {[hover.when, hover.resultLine].filter(Boolean).join(' · ') || hover.label}
                    </i>
                    <span>
                        {valueLabel || 'Value'}: {valueText}
                    </span>
                </InsightsFixedTip>
            ) : null}
            <svg
                ref={chartRef}
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                className="insightsTrendSvg"
                role="img"
                aria-label="Player trend"
            >
                <path d={path} className="insightsTrendLine" />
                {points.map((p, i) => (
                    <g key={p.matchId}>
                        <circle
                            cx={xAt(i)}
                            cy={yAt(p.value)}
                            r={hoverIndex === i ? 5 : 3.5}
                            className={`insightsTrendDot${hoverIndex === i ? ' isActive' : ''}`}
                        />
                        <circle
                            cx={xAt(i)}
                            cy={yAt(p.value)}
                            r={10}
                            className="insightsTrendHit"
                            onMouseEnter={() => setHoverIndex(i)}
                            onMouseLeave={() => setHoverIndex(null)}
                        />
                        <text
                            x={xAt(i)}
                            y={HEIGHT - 8}
                            textAnchor="middle"
                            className="insightsTrendLabel"
                        >
                            {p.label}
                        </text>
                    </g>
                ))}
            </svg>
        </div>
    );
};

export default InsightsPlayerTrendChart;
